import { Box, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import {
  ReactElement,
  MouseEvent as ReactMouseEvent,
  ReactNode,
  PointerEvent as ReactPointerEvent,
  WheelEvent as ReactWheelEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Link } from 'react-router-dom';

export type JobsiteTimelineEvent = {
  id: string;
  // ISO timestamp
  timestamp: string;
  label: string;
  icon?: ReactNode;
  href?: string;
  type: string; // delivery | recovery | return | cancellation
  // mouseover breakdown of the label, e.g. a per-category count table
  tooltip?: ReactNode;
  // optional line colour for the icon (e.g. by event type); omitted means the
  // default text colour
  color?: string;
};

type PositionedEvent = {
  event: JobsiteTimelineEvent;
  x: number;
  side: 'above' | 'below';
  level: number;
};

const NODE_WIDTH = 120;
const SIDE_HEIGHT = 96; // vertical space per side of the axis
const BRANCH_BASE = 22; // connector length at level 0
const BRANCH_STEP = 34; // extra connector length per stagger level
const PADDING_X = 64;
const MIN_WIDTH = 480;
const SLOT_MAX = NODE_WIDTH / 2 + 24; // spacing between events on quiet sites
const SLOT_MIN = 56; // floor for busy sites; below this labels would stack too deep
const SLOT_SHRINK_START = 10; // node count where spacing starts tightening

// Horizontal left→right timeline of events at a jobsite. Purely presentational:
// data comes in via props, the component only renders. Every event is always
// displayed — nothing is capped or summarized away; long histories scroll
// horizontally. Events sit at fixed intervals from the left in timestamp order
// (not proportional to elapsed time, so sparse histories don't stretch and
// bursts don't pile up), and the interval tightens on busy sites. Events
// alternate above/below the center axis to avoid label collisions — the
// alternation is layout only, both sides are styled identically; when
// tightened nodes would still overlap, they shift to staggered branch heights
// so text never collides.
export const JobsiteActivityTimeline = ({ events }: { events: JobsiteTimelineEvent[] }): ReactElement => {
  const { nodes, width, maxLevel } = useMemo(() => {
    const sorted = [...events].filter((event) => event.timestamp).sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    if (sorted.length === 0) {
      return { nodes: [] as PositionedEvent[], width: MIN_WIDTH, maxLevel: 0 };
    }
    // 84px apart up to 10 nodes, then 2px tighter per extra node down to 56px.
    const slot = Math.max(SLOT_MIN, SLOT_MAX - Math.max(0, sorted.length - SLOT_SHRINK_START) * 2);
    const width = Math.max(MIN_WIDTH, PADDING_X + (sorted.length - 1) * slot + NODE_WIDTH / 2 + PADDING_X);

    // Per side, remember the last node x at each stagger level; when a new node
    // would land within NODE_WIDTH of it, push the new node one level further out.
    const lastXPerLevel: Record<'above' | 'below', number[]> = { above: [], below: [] };
    let maxLevel = 0;
    const nodes = sorted.map((event, index) => {
      const x = PADDING_X + index * slot;
      const side: 'above' | 'below' = index % 2 === 0 ? 'above' : 'below';
      let level = 0;
      while (lastXPerLevel[side][level] !== undefined && x - lastXPerLevel[side][level] < NODE_WIDTH) {
        level += 1;
      }
      lastXPerLevel[side][level] = x;
      maxLevel = Math.max(maxLevel, level);
      return { event, x, side, level };
    });
    return { nodes, width, maxLevel };
  }, [events]);

  const sideHeight = SIDE_HEIGHT + maxLevel * BRANCH_STEP;
  const height = sideHeight * 2 + 2;

  // Drag-to-pan + wheel-to-horizontal so a busy timeline is obviously scrollable
  // (a bare scrollbar isn't a strong enough affordance, and macOS hides it).
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const drag = useRef({ active: false, startX: 0, startScroll: 0, moved: false });
  const [scrollable, setScrollable] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const check = (): void => setScrollable(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);

  // Default the view to the most recent activity (rightmost node) on load and
  // whenever the timeline relayouts.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [width]);

  const onPointerDown = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    drag.current = { active: true, startX: e.clientX, startScroll: el.scrollLeft, moved: false };
    setDragging(true);
  }, []);

  const onPointerMove = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || !drag.current.active) return;
    const dx = e.clientX - drag.current.startX;
    if (Math.abs(dx) > 3) drag.current.moved = true;
    el.scrollLeft = drag.current.startScroll - dx;
  }, []);

  const endDrag = useCallback(() => {
    if (!drag.current.active) return;
    drag.current.active = false;
    setDragging(false);
  }, []);

  // Swallow the click that follows a drag so panning doesn't navigate a node link.
  const onClickCapture = useCallback((e: ReactMouseEvent<HTMLDivElement>) => {
    if (drag.current.moved) {
      e.preventDefault();
      e.stopPropagation();
      drag.current.moved = false;
    }
  }, []);

  const onWheel = useCallback((e: ReactWheelEvent<HTMLDivElement>) => {
    const el = scrollRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) el.scrollLeft += e.deltaY;
  }, []);

  if (nodes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
        No activity yet
      </Typography>
    );
  }

  // The axis line runs from just inside the first label to just inside the
  // last one, with its terminal symbols on the line ends.
  const axisStart = Math.max(0, nodes[0].x - NODE_WIDTH / 2 + 8);
  const axisEnd = nodes[nodes.length - 1].x + NODE_WIDTH / 2 - 8;

  return (
    <Box>
      {scrollable && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'right', mb: 0.5, pr: 1, fontStyle: 'italic' }}>
          drag or scroll to see more →
        </Typography>
      )}
      <Box
        ref={scrollRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onClickCapture={onClickCapture}
        onWheel={onWheel}
        sx={{
          overflowX: 'auto',
          overflowY: 'hidden',
          width: '100%',
          // Busy sites are pannable: grab-and-drag, mouse-wheel, or the scrollbar.
          // The grab cursor is the key affordance; the styled bar is always visible
          // (macOS overlay scrollbars otherwise stay hidden until scrolling).
          cursor: scrollable ? (dragging ? 'grabbing' : 'grab') : 'default',
          userSelect: dragging ? 'none' : 'auto',
          pb: 1,
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': { height: 10 },
          '&::-webkit-scrollbar-track': { backgroundColor: 'action.hover', borderRadius: 5 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'action.disabled', borderRadius: 5 },
          '&::-webkit-scrollbar-thumb:hover': { backgroundColor: 'action.active' },
        }}
      >
        <Box sx={{ position: 'relative', width, minWidth: '100%', height }}>
          {/* center time axis */}
          <Box sx={{ position: 'absolute', left: axisStart, width: axisEnd - axisStart, top: sideHeight, borderTop: 2, borderColor: 'divider' }} />
          {/* axis start: circle terminal */}
          <Box
            sx={{
              position: 'absolute',
              left: axisStart - 6,
              top: sideHeight - 5,
              width: 12,
              height: 12,
              borderRadius: '50%',
              border: 2,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          />
          {/* axis end: arrowhead, tip on the line end */}
          <Box
            sx={{
              position: 'absolute',
              left: axisEnd - 10,
              top: sideHeight - 5,
              width: 0,
              height: 0,
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
              borderLeft: '10px solid',
              borderLeftColor: 'divider',
            }}
          />
          {nodes.map(({ event, x, side, level }) => {
            const branch = BRANCH_BASE + level * BRANCH_STEP;
            const node = (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: side === 'above' ? 'column-reverse' : 'column',
                  alignItems: 'center',
                  gap: 1,
                  width: NODE_WIDTH,
                  color: 'text.primary',
                }}
              >
                {event.color ? <Box sx={{ color: event.color, display: 'flex' }}>{event.icon}</Box> : event.icon}
                <Typography variant="caption" sx={{ textAlign: 'center', lineHeight: 1.3, whiteSpace: 'pre-line' }}>
                  {event.label}
                  <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {DateTime.fromISO(event.timestamp).toLocaleString(DateTime.DATETIME_SHORT)}
                  </Typography>
                </Typography>
              </Box>
            );
            return (
              <Box key={event.id} sx={{ position: 'absolute', left: x, top: sideHeight, width: 0 }}>
                {/* axis dot */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: -4,
                    top: -3,
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                  }}
                />
                {/* connector from axis to node */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 0,
                    height: branch,
                    ...(side === 'above' ? { bottom: 3 } : { top: 5 }),
                    borderLeft: 1,
                    borderColor: 'divider',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    left: -NODE_WIDTH / 2,
                    ...(side === 'above' ? { bottom: branch + 3 } : { top: branch + 5 }),
                  }}
                >
                  <Tooltip title={event.tooltip ?? ''} arrow disableHoverListener={!event.tooltip}>
                    {event.href ? (
                      <Link to={event.href} style={{ textDecoration: 'none', color: 'inherit' }}>
                        {node}
                      </Link>
                    ) : (
                      <Box>{node}</Box>
                    )}
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

export default JobsiteActivityTimeline;
